'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { COMPANY } from '@/data/company';
import ImagePlaceholder from '@/components/ui/ImagePlaceholder';
import { projectPhoto } from '@/lib/projectPhoto';
import { PhotoPrompt } from '@/components/ui/NextVisual';
import SitePhoto from '@/components/ui/SitePhoto';
import { SITE_PHOTOS } from '@/data/sitePhotos';
import ScrollReveal from '@/app/components/ScrollReveal';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import CTAPrimary from '@/components/ui/CTAPrimary';
import CTASecondary from '@/components/ui/CTASecondary';

// Real projects we manufactured and built (portrait ~3:4, wide ~3:2 — matched to their slots)
const PROOF_PHOTOS = [
  { wide: true, photo: projectPhoto('the-green-storey-g3-house-bannerghatta', 'white-sliding-door-wardrobe-with-lofts.jpg') },
  { wide: false, photo: projectPhoto('rasha-apartment-hosa-road-electronic-city', 'black-frame-glass-shutter-wardrobe-with-led-lighting.jpg') },
  { wide: false, photo: projectPhoto('blue-on-mars-apartment-banashankari', 'pooja-unit-with-folding-doors-and-blue-drawers.jpg') },
  { wide: true, photo: projectPhoto('gubbi-gudu-villa-anjanapura', 'floating-walnut-tv-unit-with-fluted-wood-wall-panel.jpg') },
];

// ─── DATA ─────────────────────────────────────────────────────────────────────

const PARTNERS = [
  {
    id: 'interior-designers',
    label: 'Interior Designers',
    tagline: 'You design. We build.',
    description:
      'Willow can support interior designers with the manufacturing and execution infrastructure their projects require. Bring your design intent — we provide the production capability behind it.',
    href: '/b2b/interior-designers',
    cta: 'Interior Designer Partnerships',
    number: '01',
  },
  {
    id: 'architects',
    label: 'Architects',
    tagline: 'Your design intent. Our manufacturing expertise.',
    description:
      'Willow can work with architects who need modular manufacturing and interior execution capability behind their projects. Your design authority remains intact.',
    href: '/b2b/architects',
    cta: 'Architect Partnerships',
    number: '02',
  },
  {
    id: 'builders',
    label: 'Builders & Developers',
    tagline: 'Modular interiors built for project-scale execution.',
    description:
      'Willow can work with builders and developers who need modular interior manufacturing and installation coordinated with their project schedule.',
    href: '/contact',
    cta: 'Builder Partnerships',
    number: '03',
  },
  {
    id: 'production-partner',
    label: 'Production Partner',
    tagline: 'Manufacturing behind your project.',
    description:
      'Willow can act as a production partner where the professional team retains design and client ownership while Willow supports manufacturing and execution. The design authority stays with you.',
    href: '/contact',
    cta: 'Production Partner Enquiry',
    number: '04',
  },
];

// Saral Mane was designed by Ar. Ujwal Prabhu N (Nord Studio) and built by Willow — real "your design, we build" proof
const SM_KITCHEN_RENDER = projectPhoto('saral-mane-g2-house-kanakapura-road', 'sage-green-modular-kitchen-front-view-3d-render.jpg');

const WORKFLOW_STEPS = [
  { label: 'Design', note: 'Your design intent — drawings, specifications, documentation.', photo: projectPhoto('saral-mane-g2-house-kanakapura-road', 'parents-bedroom-sliding-wardrobe-3d-render.jpg'), companions: [projectPhoto('saral-mane-g2-house-kanakapura-road', 'parents-bedroom-taupe-and-white-sliding-wardrobe.jpg')] },
  { label: 'Documentation', note: 'Production documentation prepared from design intent.', photo: SITE_PHOTOS.teamDiscussion },
  { label: 'Manufacturing', note: 'Modular components manufactured to specification.', photo: SITE_PHOTOS.clientReview },
  { label: 'Quality Control', note: 'Verification before components leave the facility.', photo: SITE_PHOTOS.clientVisit },
  { label: 'Installation', note: 'Supervised on-site installation.', photo: SITE_PHOTOS.floorProtection, companions: [SITE_PHOTOS.sitePanels] },
  { label: 'Handover', note: 'Documented completion.', photo: projectPhoto('saral-mane-g2-house-kanakapura-road', 'sage-green-l-shaped-modular-kitchen-with-ivory-wall-cabinets.jpg'), companions: [projectPhoto('saral-mane-g2-house-kanakapura-road', 'master-bedroom-with-walnut-headboard-panel-and-floating-bed.jpg')] },
];

const CAPABILITIES = [
  { label: 'Modular Manufacturing', group: 'core' },
  { label: 'Shutters', group: 'core' },
  { label: 'Laminate Work', group: 'core' },
  { label: 'Edge Banding', group: 'core' },
  { label: 'Assembly', group: 'process' },
  { label: 'Packaging', group: 'process' },
  { label: 'Quality Control', group: 'process' },
  { label: 'Installation', group: 'process' },
  { label: 'Documentation', group: 'discipline' },
  { label: 'Process Discipline', group: 'discipline' },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

// `journey`: the next-visual cards for this page, built on the server (see src/data/journeyItems.ts)
export default function B2BContent({ journey }: { journey?: React.ReactNode } = {}) {
  const [activeWorkflow, setActiveWorkflow] = useState(0);

  return (
    <div className="bg-warm-white min-h-screen">

      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Professional Partnerships' },
          ]}
        />
      </div>

      {/* ── HERO ────────────────────────────────────────────────── */}
      <section aria-labelledby="b2b-h1" className="border-b border-border overflow-hidden">
        <div className="container-willow py-16 md:py-0">
          <div className="grid lg:grid-cols-[1fr_1fr] min-h-[80vh] items-stretch">

            {/* Left: Text */}
            <div className="flex flex-col justify-center gap-8 py-16 lg:py-24 lg:pr-16">
              <div>
                <h1
                  id="b2b-h1"
                  style={{
                    fontSize: 'clamp(2.5rem, 6.5vw, 5rem)',
                    fontWeight: 700,
                    lineHeight: 1.0,
                    letterSpacing: '-0.04em',
                    color: 'var(--foreground)',
                  }}
                >
                  {/* Topic line inside the H1 so the heading says what the page is about; slogan below */}
                  <span className="flex items-center gap-3 mb-8 leading-normal">
                    <span className="accent-line" aria-hidden="true" />
                    <span className="text-section-label">Manufacturing Partner for Designers &amp; Architects</span>
                  </span>
                  Have the<br />
                  <span style={{ color: 'var(--primary)' }}>Design?</span>
                </h1>
                <h2
                  style={{
                    fontSize: 'clamp(2.5rem, 6.5vw, 5rem)',
                    fontWeight: 700,
                    lineHeight: 1.0,
                    letterSpacing: '-0.04em',
                    color: 'var(--foreground)',
                    marginTop: '0.1em',
                  }}
                >
                  We Can Build It.
                </h2>
              </div>

              <p className="text-body-lg" style={{ maxWidth: '34rem' }}>
                Willow works with interior designers, architects, builders and professional project teams who need manufacturing and execution capability behind their projects.
              </p>

              <p className="text-body" style={{ maxWidth: '34rem', color: 'var(--secondary)', opacity: 0.8 }}>
                You don&apos;t necessarily need to change how you design. Willow provides the manufacturing and execution infrastructure to build it.
              </p>

              {/* Capability chain */}
              <div className="flex items-center gap-2 flex-wrap pt-2">
                {['DESIGN UNDERSTANDING', 'MANUFACTURING', 'EXECUTION']?.map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <span
                      className="text-section-label"
                      style={{
                        color: step === 'MANUFACTURING' ? 'var(--primary)' : 'var(--secondary)',
                        opacity: step === 'MANUFACTURING' ? 1 : 0.65,
                      }}
                    >
                      {step}
                    </span>
                    {i < arr?.length - 1 && (
                      <span className="text-section-label" style={{ color: 'var(--primary)', opacity: 0.35 }} aria-hidden="true">+</span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <CTAPrimary label="Discuss a Project" href="/contact" />
                <CTASecondary label="View Projects" href="/projects" />
              </div>
            </div>

            {/* Right: Hero image */}
            <div className="relative hidden lg:block" style={{ minHeight: '600px' }}>
              <SitePhoto photo={SITE_PHOTOS.studioKitchen} aspectRatio="2/3" fillParent className="w-full h-full absolute inset-0" priority />
              {/* Overlay label */}
              <div
                className="absolute bottom-8 left-8 px-4 py-2"
                style={{ backgroundColor: 'rgba(23,22,20,0.72)', backdropFilter: 'blur(4px)' }}
              >
                <span className="text-section-label" style={{ color: '#AA7D53' }}>
                  WILLOW · MANUFACTURING &amp; EXECUTION
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOUR PARTNERSHIP PATHWAYS ───────────────────────────── */}
      <section aria-labelledby="partners-heading" className="border-b border-border">
        <div className="container-willow py-16 md:py-24">
          <ScrollReveal>
            <div className="grid md:grid-cols-[1fr_1.5fr] gap-10 md:gap-20 items-end mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="accent-line" aria-hidden="true" />
                  <span className="text-section-label">WHO WE WORK WITH</span>
                </div>
                <h2 id="partners-heading" className="text-display">
                  Four Partnership<br />Pathways.
                </h2>
              </div>
              <p className="text-body-lg">
                Willow combines design understanding, manufacturing and execution capability. Professional partners work with Willow when they need that infrastructure behind their projects.
              </p>
            </div>
          </ScrollReveal>

          {/* Partner cards — asymmetric bento */}
          <div className="grid md:grid-cols-2 gap-px bg-border">
            {PARTNERS?.map((partner, idx) => (
              <ScrollReveal key={partner?.id}>
                <Link
                  href={partner?.href}
                  className="group block bg-warm-white p-8 md:p-10 hover:bg-stone-50 transition-colors duration-200"
                  style={{
                    minHeight: idx === 0 || idx === 3 ? '360px' : '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                  aria-label={`${partner?.label} — ${partner?.tagline}`}
                >
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start justify-between">
                      <span
                        className="text-section-label"
                        style={{ color: 'var(--primary)', opacity: 0.5 }}
                      >
                        {partner?.number}
                      </span>
                      {/* Arrow */}
                      <span
                        className="text-section-label transition-transform duration-200 group-hover:translate-x-1"
                        style={{ color: 'var(--primary)' }}
                        aria-hidden="true"
                      >
                        →
                      </span>
                    </div>

                    <div>
                      <h3
                        className="font-semibold mb-2"
                        style={{
                          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                          letterSpacing: '-0.02em',
                          color: 'var(--foreground)',
                        }}
                      >
                        {partner?.label}
                      </h3>
                      <p
                        className="text-section-label mb-4"
                        style={{ color: 'var(--primary)', opacity: 0.8 }}
                      >
                        {partner?.tagline}
                      </p>
                      <p className="text-body" style={{ maxWidth: '28rem' }}>
                        {partner?.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-border">
                    <span
                      className="text-section-label group-hover:opacity-100 transition-opacity duration-200"
                      style={{ color: 'var(--primary)', opacity: 0.7 }}
                    >
                      {partner?.cta} →
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WORKING WITH EXISTING DESIGN ────────────────────────── */}
      <section
        aria-labelledby="existing-design-heading"
        style={{ backgroundColor: '#171614' }}
      >
        <div className="container-willow py-16 md:py-24">
          <ScrollReveal>
            <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center">

              {/* Left: Image */}
              <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <SitePhoto photo={SM_KITCHEN_RENDER} aspectRatio="4/3" sizes="(min-width: 1024px) 50vw, 100vw" />
                <Link
                  href={SM_KITCHEN_RENDER.projectHref}
                  className="absolute bottom-0 left-0 right-0 px-6 py-4 text-xs hover:underline"
                  style={{ background: 'linear-gradient(to top, rgba(23,22,20,0.75) 0%, transparent 100%)', color: '#FAF8F5' }}
                >
                  Design by Ar. Ujwal Prabhu N, Nord Studio — built by Willow ({SM_KITCHEN_RENDER.projectName}) →
                </Link>
              </div>

              {/* Right: Text */}
              <div className="flex flex-col gap-7">
                <div className="flex items-center gap-3">
                  <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                  <span className="text-section-label" style={{ color: '#AA7D53' }}>WORKING WITH EXISTING DESIGN</span>
                </div>

                <h2
                  id="existing-design-heading"
                  className="text-display"
                  style={{ color: '#FAF8F5' }}
                >
                  Your Design<br />Authority Stays<br />With You.
                </h2>

                <blockquote
                  style={{
                    borderLeft: '2px solid #AA7D53',
                    paddingLeft: '1.25rem',
                    color: 'rgba(250,248,245,0.85)',
                    fontStyle: 'italic',
                    fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
                    lineHeight: 1.6,
                  }}
                >
                  Willow can work with a professional team&apos;s existing design intent. You don&apos;t need to adopt a Willow design system.
                </blockquote>

                <div className="flex flex-col gap-4">
                  <p className="text-body" style={{ color: 'rgba(250,248,245,0.65)' }}>
                    The professional partner remains the design authority. Willow contributes manufacturing and execution capability — behind the design, not in front of it.
                  </p>
                  <p className="text-body" style={{ color: 'rgba(250,248,245,0.65)' }}>
                    Bring your drawings, specifications and documentation. Willow translates design intent into production.
                  </p>
                </div>

                <div className="pt-2">
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 text-section-label hover:opacity-100 transition-opacity duration-200"
                    style={{ color: '#AA7D53', opacity: 0.85 }}
                  >
                    Discuss a collaboration →
                  </Link>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── HOW THE COLLABORATION WORKS ─────────────────────────── */}
      <section aria-labelledby="workflow-heading" className="border-b border-border">
        <div className="container-willow py-16 md:py-24">
          <ScrollReveal>
            <div className="grid md:grid-cols-[1fr_1.5fr] gap-10 md:gap-20 items-end mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="accent-line" aria-hidden="true" />
                  <span className="text-section-label">THE WORKFLOW</span>
                </div>
                <h2 id="workflow-heading" className="text-display">
                  How the<br />Collaboration Works.
                </h2>
              </div>
              <p className="text-body-lg">
                A professional collaboration with Willow follows a clear sequence — from design intent through to handover.
              </p>
            </div>
          </ScrollReveal>

          {/* Workflow — interactive horizontal selector */}
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">

            {/* Step list */}
            <div className="flex flex-col">
              {WORKFLOW_STEPS?.map((step, idx) => (
                <button
                  key={step?.label}
                  type="button"
                  onClick={() => setActiveWorkflow(idx)}
                  className="group text-left py-5 border-b border-border transition-colors duration-150"
                  style={{
                    borderLeftWidth: activeWorkflow === idx ? '2px' : '0',
                    borderLeftColor: 'var(--primary)',
                    paddingLeft: activeWorkflow === idx ? '1.25rem' : '0',
                    transition: 'padding-left 0.2s ease, border-left-width 0.2s ease',
                  }}
                  aria-pressed={activeWorkflow === idx}
                  aria-label={`Step ${idx + 1}: ${step?.label}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span
                        className="text-section-label flex-shrink-0"
                        style={{
                          color: activeWorkflow === idx ? 'var(--primary)' : 'var(--secondary)',
                          opacity: activeWorkflow === idx ? 1 : 0.4,
                        }}
                      >
                        {String(idx + 1)?.padStart(2, '0')}
                      </span>
                      <span
                        className="font-semibold"
                        style={{
                          fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
                          letterSpacing: '-0.01em',
                          color: activeWorkflow === idx ? 'var(--foreground)' : 'var(--secondary)',
                        }}
                      >
                        {step?.label}
                      </span>
                    </div>
                    {idx < WORKFLOW_STEPS?.length - 1 && (
                      <span
                        className="text-section-label"
                        style={{ color: 'var(--primary)', opacity: 0.3 }}
                        aria-hidden="true"
                      >
                        ↓
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Active step detail */}
            <div className="lg:sticky lg:top-24">
              <ScrollReveal>
                <div
                  className="p-8 md:p-10"
                  style={{ backgroundColor: 'var(--muted)', minHeight: '280px' }}
                >
                  <div className="flex flex-col gap-5 h-full">
                    <span
                      className="text-section-label"
                      style={{ color: 'var(--primary)', opacity: 0.5 }}
                    >
                      {String(activeWorkflow + 1)?.padStart(2, '0')} / {String(WORKFLOW_STEPS?.length)?.padStart(2, '0')}
                    </span>
                    <h3
                      style={{
                        fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                        fontWeight: 700,
                        letterSpacing: '-0.025em',
                        color: 'var(--foreground)',
                      }}
                    >
                      {WORKFLOW_STEPS?.[activeWorkflow]?.label}
                    </h3>
                    <p className="text-body-lg">
                      {WORKFLOW_STEPS?.[activeWorkflow]?.note}
                    </p>
                  </div>
                </div>
              </ScrollReveal>

              {/* Step image — real photo where the step has one, placeholder otherwise */}
              <div className="mt-4">
                {(() => {
                  const step = WORKFLOW_STEPS?.[activeWorkflow];
                  return step && 'photo' in step && step.photo ? (
                    <SitePhoto
                      photo={step.photo}
                      companions={'companions' in step ? step.companions : undefined}
                      aspectRatio="16/9"
                      sizes="(min-width: 1024px) 60vw, 100vw"
                    />
                  ) : (
                    <ImagePlaceholder aspectRatio="16/9" label={`REAL WILLOW IMAGE REQUIRED — ${step?.label}`} />
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT WILLOW BRINGS ──────────────────────────────────── */}
      <section
        aria-labelledby="capabilities-heading"
        style={{ backgroundColor: '#171614' }}
      >
        <div className="container-willow py-16 md:py-24">
          <ScrollReveal>
            <div className="grid md:grid-cols-[1fr_1.5fr] gap-10 md:gap-20 items-end mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                  <span className="text-section-label" style={{ color: '#AA7D53' }}>WHAT WILLOW BRINGS</span>
                </div>
                <h2
                  id="capabilities-heading"
                  className="text-display"
                  style={{ color: '#FAF8F5' }}
                >
                  Manufacturing<br />&amp; Execution<br />Capability.
                </h2>
              </div>
              <p className="text-body-lg" style={{ color: 'rgba(250,248,245,0.65)' }}>
                Willow&apos;s manufacturing and execution experience comes from real interior projects. The capabilities below are what professional partners can draw on.
              </p>
            </div>
          </ScrollReveal>

          {/* Capabilities — asymmetric grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10">
            {CAPABILITIES?.map((cap) => (
              <ScrollReveal key={cap?.label}>
                <div
                  className="p-6 md:p-7 flex flex-col justify-between"
                  style={{
                    backgroundColor: '#1E1C1A',
                    minHeight: '120px',
                  }}
                >
                  <span
                    className="block h-px w-6 mb-4"
                    style={{ backgroundColor: '#AA7D53', opacity: 0.4 }}
                    aria-hidden="true"
                  />
                  <span
                    className="font-medium"
                    style={{
                      fontSize: '0.875rem',
                      letterSpacing: '0.01em',
                      color: 'rgba(250,248,245,0.85)',
                      lineHeight: 1.4,
                    }}
                  >
                    {cap?.label}
                  </span>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Links to supporting pages */}
          <ScrollReveal>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <Link
                href="/manufacturing"
                className="text-section-label hover:opacity-100 transition-opacity duration-200"
                style={{ color: '#AA7D53', opacity: 0.75 }}
              >
                Explore Manufacturing →
              </Link>
              <Link
                href="/modular-interiors"
                className="text-section-label hover:opacity-100 transition-opacity duration-200"
                style={{ color: '#AA7D53', opacity: 0.75 }}
              >
                Modular Interiors →
              </Link>
              <Link
                href="/projects"
                className="text-section-label hover:opacity-100 transition-opacity duration-200"
                style={{ color: '#AA7D53', opacity: 0.75 }}
              >
                View Projects →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── PROOF / PROJECTS ────────────────────────────────────── */}
      <section aria-labelledby="proof-heading" className="border-b border-border">
        <div className="container-willow py-16 md:py-24">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-line" aria-hidden="true" />
              <span className="text-section-label">THE WORK</span>
            </div>
            <div className="grid md:grid-cols-[1fr_1fr] gap-10 md:gap-20 items-end mb-12">
              <h2 id="proof-heading" className="text-display">
                Built From<br />Real Projects.
              </h2>
              <p className="text-body-lg">
                Willow&apos;s manufacturing and execution experience comes from real interior projects — not theoretical capability. The work is the proof.
              </p>
            </div>
          </ScrollReveal>

          {/* Real projects we manufactured and built — wide slots 3:2, tall 3:4 (same row height, no crop) */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {PROOF_PHOTOS.map((g) => (
              <ScrollReveal key={g.photo.src} className={g.wide ? 'col-span-2 md:col-span-2' : ''}>
                <Link href={g.photo.projectHref} className="group block">
                  <div className="hover-zoom overflow-hidden">
                    <SitePhoto photo={g.photo} aspectRatio={g.wide ? '3/2' : '3/4'} sizes={g.wide ? '(min-width: 768px) 66vw, 100vw' : '(min-width: 768px) 33vw, 50vw'} />
                  </div>
                  <p className="mt-2 text-caption group-hover:text-primary">{g.photo.projectName} →</p>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="mt-10 flex justify-start">
              <Link
                href="/projects"
                className="text-section-label hover:opacity-100 transition-opacity duration-200"
                style={{ color: 'var(--primary)', opacity: 0.75 }}
              >
                View all projects →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── CONTEXTUAL LINKS ────────────────────────────────────── */}
      <section aria-label="Related pages" className="border-b border-border">
        <div className="container-willow py-12 md:py-16">
          <ScrollReveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
              {[
                { label: 'Projects', href: '/projects', note: 'See the work' },
                { label: 'Manufacturing', href: '/manufacturing', note: 'How we build' },
                { label: 'Modular Interiors', href: '/modular-interiors', note: 'Our approach' },
                { label: 'Home Interiors', href: '/home-interiors', note: 'For homeowners' },
              ]?.map((link) => (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className="group bg-warm-white p-6 md:p-8 hover:bg-stone-50 transition-colors duration-200"
                >
                  <span
                    className="block text-section-label mb-2"
                    style={{ color: 'var(--primary)', opacity: 0.5 }}
                  >
                    {link?.note}
                  </span>
                  <span
                    className="font-semibold group-hover:opacity-80 transition-opacity duration-200"
                    style={{
                      fontSize: '1rem',
                      letterSpacing: '-0.01em',
                      color: 'var(--foreground)',
                    }}
                  >
                    {link?.label} →
                  </span>
                </Link>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── NEXT VISUALS — where to look next (visual journey) ── */}
      {journey}

      {/* ── FINAL CTA ───────────────────────────────────────────── */}
      <section
        aria-labelledby="b2b-cta-heading"
        style={{ backgroundColor: '#171614' }}
      >
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                <span className="text-section-label" style={{ color: '#AA7D53' }}>WORK WITH WILLOW</span>
              </div>

              <h2
                id="b2b-cta-heading"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                  color: '#FAF8F5',
                  marginBottom: '1.5rem',
                }}
              >
                Discuss a Project<br />with Willow.
              </h2>

              <p className="text-body-lg mb-8" style={{ color: 'rgba(250,248,245,0.65)', maxWidth: '32rem' }}>
                If you&apos;re a professional team looking for manufacturing and execution capability behind your project, start with a conversation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <CTAPrimary label="Discuss a Project" href="/contact" />
                <Link
                  href={`${COMPANY.whatsapp.url}?text=Hi%2C%20I%20am%20a%20professional%20looking%20to%20discuss%20a%20partnership%20with%20Willow.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold tracking-wide border transition-colors duration-150"
                  style={{
                    borderColor: 'rgba(250,248,245,0.2)',
                    color: 'rgba(250,248,245,0.75)',
                  }}
                  aria-label="Contact Willow on WhatsApp"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

    </div>
  );
}
